import { api } from 'hooks/useApi';

/**
 * A single third-party integration the user has connected through One Auth.
 * Mirrors the connection records returned by the One vault connections API
 * (proxied through our backend so the One secret stays server-side).
 */
export interface OneAuthConnection {
  id: string;
  key: string;
  title: string | null;
  name: string | null;
  platform: string;
  environment: string;
  active: boolean;
}

interface OneAuthConnectionsResponse {
  rows: OneAuthConnection[];
  total: number;
  pages: number;
  page: number;
}

/** A HubSpot contact, normalized by our backend from the HubSpot CRM API. */
export interface HubspotContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  jobTitle: string | null;
  url: string | null;
}

/** Fields for creating a HubSpot contact (all optional, at least one required). */
export interface HubspotContactInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
}

/** Whether the user has a provisioned One workspace (org + project). */
export interface OneProvisionStatus {
  provisioned: boolean;
  organization_pid: string | null;
  project_pid: string | null;
}

export type CrmResourceType = 'project' | 'document' | 'contact';

/** Whether a Mercator resource has been pushed into the user's CRM. */
export interface CrmPushStatus {
  pushed: boolean;
  platform?: string;
  crmObjectType?: string;
  crmRecordId?: string;
  /** Deep link to the created record in the CRM (null when it can't be built). */
  crmRecordUrl?: string | null;
  status?: string;
}

/**
 * Fetch the integrations a given identity has already connected. `identity`
 * must match the value passed to the One Auth widget when opening it.
 */
const getConnections = (identity: string) => {
  return api.get<OneAuthConnectionsResponse>('/one-auth/connections/', {
    params: { identity, identityType: 'user' },
  });
};

/** Create a HubSpot contact for the given identity. */
const createHubspotContact = (identity: string, contact: HubspotContactInput) => {
  return api.post<HubspotContact>('/one-auth/hubspot/contacts/', contact, {
    params: { identity, identityType: 'user' },
  });
};

/** Permanently delete a connection (and its stored credentials) by its id. */
const deleteConnection = (connectionId: string) => {
  return api.delete(`/one-auth/connections/${connectionId}/`);
};

/** Whether the current user has a provisioned One workspace. */
const getProvisionStatus = () => {
  return api.get<OneProvisionStatus>('/one-auth/provision/');
};

/** Provision (or ensure) the current user's One organization + project. */
const provision = () => {
  return api.post<OneProvisionStatus>('/one-auth/provision/');
};

/** Push a Mercator resource into the user's connected CRM (create or update). */
/** Contact fields the frontend supplies when pushing a contact (no server source). */
export interface CrmContactInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  title?: string;
  company?: string;
}

const pushToCrm = (
  resourceType: CrmResourceType,
  resourceId: string,
  contact?: CrmContactInput,
) => {
  return api.post('/one-auth/crm/push/', {
    resourceType,
    resourceId,
    ...(contact ? { contact } : {}),
  });
};

/** Whether the user has already pushed a given resource to their CRM. */
const getCrmPushStatus = (resourceType: CrmResourceType, resourceId: string) => {
  return api.get<CrmPushStatus>('/one-auth/crm/push/', {
    params: { resourceType, resourceId },
  });
};

export default {
  getConnections,
  createHubspotContact,
  deleteConnection,
  getProvisionStatus,
  provision,
  pushToCrm,
  getCrmPushStatus,
};
